import { useEffect, useMemo, useState } from "react";
import { IoMdCloseCircle } from "react-icons/io";
import { startSupportAccess } from "../../services/startSupportAccess.js";
import styles from "./css/supportAccessModal.module.css";

const REASON_MIN = 10;
const REASON_MAX = 300;

const BLOCK_LABELS = {
  PAYMENT_PENDING: "Pagamento pendente",
  NOT_OPERATING: "Bloqueada",
};

const CLOSED_LABELS = {
  COMPANY_DELETED: "Empresa excluída",
  COMPANY_INCOMPLETE: "Dados da empresa incompletos",
};

// MESMA NORMALIZAÇÃO DA API (colapsa espaços e quebras de linha) PARA O CONTADOR NÃO DIVERGIR DA VALIDAÇÃO
const normalizeReason = (value) => value.replace(/\s+/g, " ").trim();

function getStatus(binding) {
  const access = binding.supportAccess;

  if (!access?.allowed) {
    return { label: CLOSED_LABELS[access?.reason] ?? "Indisponível", className: styles.badgeOff };
  }

  if (access.companyBlock) {
    return { label: BLOCK_LABELS[access.companyBlock] ?? "Bloqueada", className: styles.badgeWarn };
  }

  return { label: "Ativa", className: styles.badgeOk };
}

function formatCargo(cargo) {
  if (!cargo) return "Sem cargo";
  if (cargo.toLowerCase() === "owner") return "Dono";
  return cargo;
}


// SELEÇÃO DA EMPRESA + MOTIVO PARA ABRIR UMA SESSÃO DE SUPORTE NA CONTA DO USUÁRIO (specs/ACESSO-ADMIN-CONTA-CLIENTE.spec.md, 8.3)
export default function SupportAccessModal({ show, user, bindings, onClose }) {
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { sessionMinutes, companyName, blockedUrl }

  const allowedBindings = useMemo(
    () => (bindings ?? []).filter((item) => item.supportAccess?.allowed),
    [bindings]
  );

  // A CADA ABERTURA COMEÇA LIMPO; COM UMA ÚNICA EMPRESA PERMITIDA, ELA JÁ VEM SELECIONADA
  useEffect(() => {
    if (!show) return;

    setReason("");
    setError(null);
    setResult(null);
    setLoading(false);
    setSelectedCompanyId(allowedBindings.length === 1 ? allowedBindings[0].companyId : null);
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show) return null;

  const selected = allowedBindings.find((item) => item.companyId === selectedCompanyId) ?? null;
  const reasonLength = normalizeReason(reason).length;
  const reasonValid = reasonLength >= REASON_MIN && reasonLength <= REASON_MAX;
  const canConfirm = !!selected && reasonValid && !loading && !result;

  const handleConfirm = async () => {
    if (!canConfirm) return;

    // A ABA É ABERTA JÁ NO CLIQUE: DEPOIS DE UM `await` O NAVEGADOR PODE BLOQUEAR O POP-UP
    const tab = window.open("about:blank", "_blank");

    try {
      setLoading(true);
      setError(null);

      const data = await startSupportAccess({
        userId: user.id,
        companyId: selected.companyId,
        reason: normalizeReason(reason),
      });

      let blockedUrl = null;

      if (tab) {
        tab.opener = null;
        tab.location.href = data.url;

      } else {
        // O LINK ALTERNATIVO SÓ APARECE QUANDO A ABA FOI BLOQUEADA (UM TICKET SERVE UMA ÚNICA VEZ)
        blockedUrl = data.url;
      }

      setResult({
        sessionMinutes: data.sessionMinutes,
        companyName: data.target?.companyName ?? selected.companyName,
        blockedUrl,
      });

    } catch (err) {
      console.error("ERROR - START SUPPORT ACCESS FUNCTION:", err?.response?.status);

      if (tab) tab.close();

      setError(err?.response?.data?.error || "Não foi possível iniciar o acesso de suporte no momento.");

    } finally {
      setLoading(false);
    }
  };

  // IMPEDE QUE O CLIQUE CHEGUE AO OVERLAY DO MODAL DO USUÁRIO (QUE FECHARIA OS DOIS)
  return (
    <div className={styles.modalOverlay} onClick={(event) => event.stopPropagation()}>
      <div className={styles.modalContainer}>
        <header className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Acessar conta de {user?.name || "usuário"}</h2>

          <button type="button" className={styles.closeButton} onClick={onClose}>
            <IoMdCloseCircle size={24} color="#2f3e49" />
          </button>
        </header>

        <div className={styles.modalBody}>
          {result ? (
            <div className={styles.successBox}>
              <p>
                <b>Sessão de suporte aberta</b> na empresa <b>{result.companyName}</b>.
                {result.blockedUrl
                  ? " O navegador bloqueou a nova aba; use o link abaixo (vale uma única vez)."
                  : " Ela foi aberta em uma nova aba."}
              </p>

              <p>A sessão vale por {result.sessionMinutes} min e fica registrada na tela de Logs.</p>

              {result.blockedUrl && (
                <a className={styles.fallbackLink} href={result.blockedUrl} target="_blank" rel="noopener noreferrer">
                  Abrir sessão
                </a>
              )}
            </div>

          ) : (
            <>
              <div>
                <p className={styles.sectionTitle}>Empresa</p>

                {(bindings ?? []).length === 0 ? (
                  <p className={styles.feedbackText}>Este usuário não possui nenhum vínculo registrado.</p>

                ) : (
                  <div className={styles.companyList}>
                    {bindings.map((item) => {
                      const allowed = !!item.supportAccess?.allowed;
                      const isSelected = allowed && item.companyId === selectedCompanyId;
                      const status = getStatus(item);

                      return (
                        <button
                          key={item.id}
                          type="button"
                          disabled={!allowed}
                          className={[
                            styles.companyOption,
                            isSelected ? styles.companyOptionSelected : "",
                            !allowed ? styles.companyOptionDisabled : "",
                          ].join(" ")}
                          onClick={() => setSelectedCompanyId(item.companyId)}
                        >
                          <span className={`${styles.radioDot} ${isSelected ? styles.radioDotSelected : ""}`} />

                          <span className={styles.companyInfo}>
                            <p className={styles.companyName}>{item.companyName}</p>
                            <p className={styles.companyCargo}>{formatCargo(item.cargo)}</p>
                          </span>

                          <span className={`${styles.badge} ${status.className}`}>{status.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {selected?.supportAccess?.companyBlock && (
                <p className={styles.blockedNotice}>
                  Esta empresa está bloqueada para o cliente. O acesso é exclusivo do suporte e não a libera.
                </p>
              )}

              <div className={styles.reasonArea}>
                <p className={styles.sectionTitle}>Motivo do acesso (obrigatório)</p>

                <textarea
                  className={styles.reasonInput}
                  value={reason}
                  maxLength={REASON_MAX}
                  placeholder="Ex.: cliente pediu ajuda para cadastrar o cardápio"
                  onChange={(event) => setReason(event.target.value)}
                />

                <span className={`${styles.reasonCounter} ${reasonLength > 0 && !reasonValid ? styles.reasonCounterInvalid : ""}`}>
                  {reasonLength} / {REASON_MAX} (mínimo {REASON_MIN})
                </span>
              </div>

              <p className={styles.infoNotice}>
                Você entrará como este usuário, com acesso total à empresa, por tempo limitado. A sessão e as
                alterações feitas nela ficam registradas. Ações que pedem a senha do usuário não funcionam nessa sessão.
              </p>

              {error && <p className={styles.errorText}>{error}</p>}
            </>
          )}
        </div>

        <footer className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            {result ? "Fechar" : "Cancelar"}
          </button>

          {!result && (
            <button type="button" className={styles.confirmButton} disabled={!canConfirm} onClick={handleConfirm}>
              {loading ? "Abrindo..." : "Acessar"}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
